import { Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from 'environment';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { FeatureService } from '@services/feature-toggle/feature.service';
import { AW } from '@airwallet/types';
import { HttpService } from '@services/helper/http.service';
import { FeatureAccessKeys } from '@airwallet/data';

@Injectable({
    providedIn: 'root'
})
export class ContractService {
    constructor(
        private http: HttpClient,
        private featureService: FeatureService,
        private httpService: HttpService
    ) {}

    loadingContracts: WritableSignal<boolean> = signal(false);
    contractFeatureAccessKeys: WritableSignal<FeatureAccessKeys> = signal({
        featureCategoryKey: 'operator_contract',
        featureActionKey: 'limit',
        currentTotal: 0
    });

    async getExpiringContracts() {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(this.http.get(`${environment.baseUrl}/api_operator/contract/overview/expire`))
                .then(res => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async getPerformanceContracts(params: string) {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(this.http.get(`${environment.baseUrl}/api_operator/contract/overview/performance?${params}`))
                .then(res => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async getContracts(params: string): Promise<AW.Operator.Contract.ContractResponse> {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(this.http.get<AW.Operator.Contract.ContractResponse>(`${environment.baseUrl}/api_operator/contract/all?${params}`))
                .then(res => {
                    const numberOfContracts = res.info.contract_count;
                    if (this.featureService.isFeatureNumberLimitReached(this.contractFeatureAccessKeys().featureCategoryKey, this.contractFeatureAccessKeys().featureActionKey, numberOfContracts)) {
                        this.contractFeatureAccessKeys.set({ ...this.contractFeatureAccessKeys(), currentTotal: numberOfContracts });
                    }
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async getSpecificContractInformation(uid: string) {
        return this.httpService.dynamicHttp(`api_operator/location`, 'get', { onBehalf: uid });
    }

    async getAllContractInformation() {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(this.http.get(`${environment.baseUrl}/api_operator/location/all`))
                .then((res: any) => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async createOrEditContract(contract: AW.Operator.Contract, selectedUid: string, isEdit: boolean, fromDate: number, toDate: number) {
        if (isEdit) {
            return await this.editContract(contract, selectedUid, fromDate, toDate);
        } else {
            return await this.createContract(contract, selectedUid, fromDate, toDate);
        }
    }

    async createContract(contract: AW.Operator.Contract, selectedUid: string, fromDate: number, toDate: number) {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(this.http.post(`${environment.baseUrl}/api_operator/contract`, { contract, selectedUid, fromDate, toDate }))
                .then((res: any) => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async editContract(contract: AW.Operator.Contract, selectedUid: string, fromDate: number, toDate: number) {
        return new Promise(async (resolve: any, reject: any) => {
            return lastValueFrom(this.http.put(`${environment.baseUrl}/api_operator/contract`, { contract, selectedUid, fromDate, toDate }))
                .then((res: any) => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }

    async deleteContract(selectedUid: string, contract_key: string) {
        return new Promise(async (resolve: any, reject: any) => {
            const options = {
                body: { selectedUid, contract_key }
            };
            return lastValueFrom(this.http.delete(`${environment.baseUrl}/api_operator/contract`, options))
                .then((res: any) => {
                    return resolve(res);
                })
                .catch(err => {
                    return reject(err);
                });
        });
    }
}
